import type { ChangeEvent, InputHTMLAttributes, Ref } from 'react';

import { useControlled } from '@/shared/hooks/useControlled';
import { Flex } from '@/shared/ui/Flex';

import styles from './Switch.module.css';

type SwitchInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'>;

export interface SwitchProps extends SwitchInputProps {
  /**
   * @default 'primary'
   */
  color?: 'standard' | 'primary' | 'inverse';
  ref?: Ref<HTMLInputElement>;

  onChange?: (checked: boolean, e: ChangeEvent<HTMLInputElement>) => void;
}

const Switch = (props: SwitchProps) => {
  const { checked: checkedProp, defaultChecked, color = 'primary', disabled, onChange, ref, ...rest } = props;

  const [checked, setChecked] = useControlled({
    state: checkedProp,
    defaultState: defaultChecked ?? false,
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.currentTarget.checked;

    setChecked(checked);
    onChange?.(checked, e);
  };

  return (
    <Flex as="label" className={styles.switchWrapper} align="center" justify="center" data-color={color}>
      <input
        ref={ref}
        role="switch"
        type="checkbox"
        className={styles.input}
        {...rest}
        checked={checked}
        disabled={disabled}
        onChange={handleChange}
      />
      <Flex className={styles.track} align="center">
        <div className={styles.thumb} />
      </Flex>
    </Flex>
  );
};

Switch.displayName = 'Switch';

export default Switch;
